import { useState } from "react";
import { Link } from "react-router-dom";
import { Stethoscope, Pill, Microscope, Building2, Baby, GraduationCap, Hospital, Landmark, Users, Check, RotateCcw, MonitorSmartphone, ArrowRight } from "lucide-react";
import { OFFRES } from "../../offres";
import { fmt, ttc } from "./OffrePage";

const STRUCTURES = [
  { id: "cabinet", label: "Cabinet médical", icon: Stethoscope },
  { id: "pharmacie", label: "Pharmacie", icon: Pill },
  { id: "laboratoire", label: "Laboratoire d'analyses", icon: Microscope },
  { id: "clinique", label: "Clinique privée", icon: Building2 },
  { id: "maternite", label: "Maternité / gynécologie", icon: Baby },
  { id: "formation", label: "Établissement de formation", icon: GraduationCap },
  { id: "hopital", label: "Hôpital général (HG)", icon: Hospital },
  { id: "chu", label: "CHR / CHU", icon: Landmark },
];
const UTILISATEURS = [
  { id: 3, label: "1 à 3" }, { id: 5, label: "4 à 5" }, { id: 10, label: "6 à 10" }, { id: 15, label: "11 à 15" },
  { id: 20, label: "16 à 20" }, { id: 25, label: "21 à 25" }, { id: 30, label: "26 à 30" }, { id: 50, label: "Plus de 30" },
];
const OPTIONS_CHIR = [
  { id: "aucun", label: "Pas de bloc opératoire" }, { id: "bloc", label: "Bloc opératoire" }, { id: "chirurgie", label: "Chirurgie dominante" },
];
const OPTIONS_ENSEIGNEMENT = [
  { id: "non", label: "Non, activité de soins uniquement" }, { id: "oui", label: "Oui, nous formons des étudiants" },
];

const ESCALADE = ["cabinet-medical", "clinique-privee-essentiel", "clinique-privee-pro", "clinique-chirurgicale", "hopital-district", "chr-chu"];
const monte = (slug, users) => {
  let i = ESCALADE.indexOf(slug);
  while (i >= 0 && i < ESCALADE.length - 1 && OFFRES[ESCALADE[i]].licences < users) i++;
  return i >= 0 ? ESCALADE[i] : slug;
};

export function recommander({ structure, users, option }) {
  const chirurgical = structure === "clinique" || structure === "hopital";
  if (!chirurgical && option === "oui") {
    return structure === "chu" || users > 15
      ? { slug: "chr-chu", raison: "Un établissement universitaire ou de grande taille qui forme des étudiants : tous les modules, Assistant Pédagogique IA et rapports de direction." }
      : { slug: "etablissement-formation", raison: "Vous formez des étudiants : la plateforme clinique avec Assistant Pédagogique IA, jusqu'à 15 utilisateurs." };
  }
  let slug, raison;
  switch (structure) {
    case "cabinet": slug = monte("cabinet-medical", users); raison = users <= 3 ? "Un cabinet solo ou un petit cabinet : l'essentiel du dossier patient pour 3 utilisateurs, tablette incluse." : "Votre équipe dépasse 3 utilisateurs : la formule clinique couvre l'admission, l'infirmerie et la facturation."; break;
    case "pharmacie": slug = "pharmacie-hospitaliere"; raison = "Stock, approvisionnement, dispensation et facturation : la formule dédiée à la pharmacie d'établissement."; break;
    case "laboratoire": slug = "laboratoire-analyses"; raison = "Résultats, comptes rendus signés et facturation : la formule dédiée au laboratoire d'analyses."; break;
    case "maternite": slug = users > 10 ? monte("clinique-privee-pro", users) : "maternite-gynecologie"; raison = users > 10 ? "Au-delà de 10 utilisateurs, la clinique complète intègre le bloc, l'anesthésie et la gestion RH." : "Le parcours mère-enfant complet : sage-femme, pédiatrie, comptes rendus et facturation."; break;
    case "formation": slug = users > 15 ? "chr-chu" : "etablissement-formation"; raison = "Les étudiants pratiquent sur un vrai SIH avec l'Assistant Pédagogique IA."; break;
    case "clinique":
      slug = option === "chirurgie" ? "clinique-chirurgicale" : option === "bloc" ? "clinique-privee-pro" : users > 10 ? "clinique-privee-pro" : "clinique-privee-essentiel";
      slug = monte(slug, users);
      raison = option === "chirurgie" ? "Chirurgie dominante : dossier opératoire, salle de réveil et anesthésie avancée." : option === "bloc" ? "Un bloc opératoire : programmation, anesthésie, stock et gestion RH intégrés." : users > 10 ? "Plus de 10 utilisateurs : la formule Pro avec ses 10 modules." : "L'essentiel de la clinique pour une équipe de 10 utilisateurs.";
      break;
    case "hopital": slug = monte("hopital-district", users); raison = "Tous les modules cliniques, urgences, maternité, laboratoire, pharmacie et direction médicale."; break;
    default: slug = "chr-chu"; raison = "La solution intégrale : tous les modules, rapports de direction et gestion des licences VLS.";
  }
  return { slug, raison };
}

function Choix({ items, value, onPick, testPrefix }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {items.map(({ id, label, icon: Icon }) => (
        <button key={id} onClick={() => onPick(id)} data-testid={`${testPrefix}-${id}`}
          className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors ${value === id ? "border-[#0369A1] bg-[#0369A1] text-white" : "border-[#7DC4E8] bg-white text-slate-700 hover:bg-[#E0F2FE]"}`}>
          {Icon ? <Icon size={16} className="shrink-0" /> : <Users size={16} className="shrink-0" />}{label}
        </button>
      ))}
    </div>
  );
}

export function GuideChoixOffre() {
  const [structure, setStructure] = useState(null);
  const [users, setUsers] = useState(null);
  const [option, setOption] = useState(null);
  const step = !structure ? 1 : users === null ? 2 : option === null ? 3 : 4;
  const reset = () => { setStructure(null); setUsers(null); setOption(null); };
  const chirurgical = structure === "clinique" || structure === "hopital";
  const reco = step === 4 ? recommander({ structure, users, option }) : null;
  const offre = reco && OFFRES[reco.slug];

  return (
    <div id="guide" className="rounded-2xl border-2 border-[#0369A1] bg-white p-6 shadow-sm sm:p-8" data-testid="guide-choix-offre">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Guide de choix · 3 clics</p>
          <h2 className="mt-1 font-display text-xl font-bold text-[#0C4A6E] sm:text-2xl">Quelle offre Vitalis pour votre structure ?</h2>
        </div>
        <div className="flex items-center gap-1.5" data-testid="guide-steps">
          {[1, 2, 3].map((n) => <span key={n} className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${step > n ? "bg-[#0369A1] text-white" : step === n ? "border-2 border-[#0369A1] text-[#0369A1]" : "border border-slate-300 text-slate-400"}`}>{step > n ? <Check size={14} /> : n}</span>)}
        </div>
      </div>

      {step === 1 && <div className="mt-6" data-testid="guide-step-1"><p className="mb-3 text-sm font-semibold text-navy">1. Quel type de structure êtes-vous ?</p><Choix items={STRUCTURES} value={structure} onPick={setStructure} testPrefix="guide-structure" /></div>}
      {step === 2 && <div className="mt-6" data-testid="guide-step-2"><p className="mb-3 text-sm font-semibold text-navy">2. Combien d'utilisateurs auront un accès ?</p><Choix items={UTILISATEURS} value={users} onPick={setUsers} testPrefix="guide-users" /></div>}
      {step === 3 && (
        <div className="mt-6" data-testid="guide-step-3">
          <p className="mb-3 text-sm font-semibold text-navy">3. {chirurgical ? "Quelle est votre activité chirurgicale ?" : "Formez-vous des étudiants en santé sur site ?"}</p>
          <Choix items={chirurgical ? OPTIONS_CHIR : OPTIONS_ENSEIGNEMENT} value={option} onPick={setOption} testPrefix="guide-option" />
        </div>
      )}

      {step === 4 && offre && (
        <div className="mt-6 grid gap-6 rounded-2xl bg-[#E0F2FE] p-5 lg:grid-cols-5" data-testid="guide-result">
          <img src={offre.image} alt={`Vitalis ${offre.nom}`} className="w-full rounded-xl border border-[#7DC4E8] object-cover lg:col-span-2" />
          <div className="lg:col-span-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Notre recommandation · Niveau {offre.niveau}</p>
            <h3 className="mt-1 font-display text-2xl font-bold text-[#0C4A6E]" data-testid="guide-result-title">Vitalis {offre.nom}</h3>
            <p className="mt-2 text-sm text-slate-700" data-testid="guide-result-raison">{reco.raison}</p>
            <p className="mt-3 font-display text-2xl font-bold text-[#0369A1]">{fmt(offre.prixHt)} <span className="text-sm font-semibold">FCFA HT / mois</span> <span className="text-xs font-normal text-slate-600">· {fmt(ttc(offre.prixHt, offre.tva))} TTC</span></p>
            <p className="mt-1 text-xs text-slate-600">{offre.licences} licences · {offre.modules.length} modules · tablette et support inclus</p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link to={`/offres/${offre.slug}#demo`} data-testid="guide-result-demo" className="inline-flex items-center gap-1.5 rounded-full bg-[#0369A1] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#075985]"><MonitorSmartphone size={15} /> Demander une démo</Link>
              <Link to={`/offres/${offre.slug}`} data-testid="guide-result-voir" className="inline-flex items-center gap-1 rounded-full border border-[#7DC4E8] bg-white px-5 py-2.5 text-sm font-semibold text-[#0369A1] hover:bg-[#F8FBFE]">Voir l'offre <ArrowRight size={14} /></Link>
              <button onClick={reset} data-testid="guide-reset" className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-slate-500 hover:text-[#0369A1]"><RotateCcw size={13} /> Recommencer</button>
            </div>
          </div>
        </div>
      )}
      {step > 1 && step < 4 && <button onClick={reset} data-testid="guide-restart" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#0369A1]"><RotateCcw size={13} /> Recommencer</button>}
    </div>
  );
}
